import type { MyLocale } from "@i18n";
import { useAppSelector } from "@store/hooks";
import { contractMigrationRequired } from "@store/selectors/contract";
import {
	differenceInDays,
	formatDistanceToNowStrict,
	formatISO,
} from "date-fns";
import { useI18n } from "next-rosetta";
import type { Address } from "viem";
import { useReadContract } from "wagmi";
import useAllNodesRewards from "./useAllNodesRewards";
import useContract from "./useContract";
import { useTokenIds } from "./useTokenIds";
export type NodeResult = {
	__id: number;
	amount: bigint;
	creationTime: bigint;
	active: boolean;
	deleted: boolean;
	lastClaimTime: bigint;
	lastCompoundTime: bigint;
	name: string;
};

export type Node = NodeResult & {
	rewards: bigint;
	created: string;
	lastClaim: string;
	differenceInDays: number;
};

export function timestampToDate(timestamp) {
	return new Date(+timestamp.toString() * 1000);
}

const useAllNodes = (account: Address) => {
	const i18n = useI18n<MyLocale>();
	const isMigrationRequired = useAppSelector(contractMigrationRequired);
	const {
		NodeController04ABI,
		NodeController05ABI,
		nodeController04Contract,
		nodeController05Contract,
	} = useContract();
	const contract = isMigrationRequired
		? nodeController04Contract
		: nodeController05Contract;
	const abi = isMigrationRequired ? NodeController04ABI : NodeController05ABI;

	const { data } = useReadContract({
		abi,
		address: contract.address as Address,
		args: [account],
		functionName: "getAllNodes",
	}) ?? { data: [] };

	// @ts-expect-error: TODO
	const nodes: Node[] = data;
	const tokenIds = useTokenIds(account);
	const activeNodes = nodes
		?.map((node, i) => ({
			...node,
			__id: isMigrationRequired ? i : tokenIds?.[i],
		}))
		?.filter((node) =>
			isMigrationRequired
				? !node.deleted && node.name !== ""
				: node.name !== "",
		);

	const nodeRewards = useAllNodesRewards(account, activeNodes);
	const retval = activeNodes?.map(
		(node, i: number) =>
			({
				...node,
				active: node.active,
				created: formatISO(
					timestampToDate(
						isMigrationRequired
							? +node?.creationTime?.toString()
							: // @ts-expect-error: TODO
								+node?.creation?.toString(),
					),
					{
						representation: "date",
					},
				),
				differenceInDays: differenceInDays(
					Date.now(),
					timestampToDate(+node?.lastClaimTime?.toString()),
				),
				lastClaim: formatDistanceToNowStrict(
					timestampToDate(+node?.lastClaimTime?.toString()),
					{
						locale: {
							formatDistance: (_, days: number) =>
								`${days} ${i18n.t(
									days === 1 ? "datetime.day" : "datetime.days",
								)}`,
						},
						roundingMethod: "floor",
						unit: "day",
					},
				),
				rewards: nodeRewards?.[i],
			}) as Node,
	);

	return retval;
};

export default useAllNodes;
